import TopNav from "@/components/feature/TopNav";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHero from "@/components/feature/PageHero";
import ContactForm from "./components/ContactForm";
import { useSeo } from "@/hooks/useSeo";
import { siteInfo, testimonials } from "@/mocks/site";

const values = [
  {
    icon: "ri-speed-up-line",
    title: "快，但不毛躁",
    desc: "热点出现后我们尽量第一时间整理，但不会为了抢时间而把未经确认的说法写成像结论一样的东西。",
  },
  {
    icon: "ri-compasses-2-line",
    title: "把来龙去脉说清",
    desc: "与其只给一句标题，我们更愿意把时间线、各方反应和争议点摆出来，让读者自己判断。",
  },
  {
    icon: "ri-plant-line",
    title: "拒绝无意义的争吵",
    desc: "娱乐话题本来就图个乐。我们不做情绪煽动，也不把网友的讨论简化成对立的口号。",
  },
];

const principles = [
  "只整理已经公开的信息与网友的公开讨论，不传播未经证实的私人内容。",
  "对仍在发酵的事件保持克制，标注清楚时间点，避免把过程写成定论。",
  "涉及具体个人时，以「公开讨论」的口径叙述，不做带有攻击性的评价。",
  "若内容出现偏差，收到反馈后会尽快核对并更新，必要时更正或下架。",
];

const aboutJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 黑料网爆",
    description: "了解黑料网爆的品牌定位、发展理念、内容原则与联系方式。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "黑料网爆",
    email: "hi@heiliaowangbao.example.com",
    description: "面向中文互联网的娱乐热点资讯聚合站，提供实时爆料、晚间热搜与新鲜事汇总。",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "/about" },
    ],
  },
];

export default function About() {
  useSeo({
    title: "关于我们 | 黑料网爆 - 品牌定位、内容原则与联系方式",
    description:
      "黑料网爆是一个把全网热点集中到一屏的娱乐资讯聚合站。了解我们的品牌定位、发展理念、内容原则、用户口碑与联系方式。",
    keywords: "黑料网爆,关于黑料网爆,内容原则,娱乐资讯聚合,联系方式",
    canonicalPath: "/about",
    jsonLd: aboutJsonLd,
  });

  return (
    <>
      <TopNav />
      <main>
        <PageHero
          crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
          eyebrow="ABOUT US"
          title="关于黑料网爆"
          desc="我们想做的其实很简单：把全网正在讨论的热点，整理成一个打开就能看完的地方。"
        />

        <section className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 lg:gap-12 items-start">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">品牌介绍</h2>
                <p className="mt-4 text-sm md:text-base leading-8 text-foreground-700">
                  {siteInfo.name}（{siteInfo.slogan}）是一个专注娱乐热点的资讯聚合站。我们把综艺、影视、音乐、品牌代言与网络热议等话题集中到同一个页面，按「实时爆料流」「晚间热搜」「刚刚发生」三种维度组织内容，让你不必在多个平台之间来回切换。
                </p>
                <p className="mt-4 text-sm md:text-base leading-8 text-foreground-700">
                  网站的更新重心放在晚间时段。很多话题在傍晚之后才会进入讨论高峰，这也是我们每天投入最多精力的时间段。
                </p>
              </div>

              <div className="rounded-lg border border-background-300/60 bg-background-100 p-6 md:p-8">
                <h2 className="font-heading text-xl font-bold text-foreground-950">网站定位</h2>
                <ul className="mt-5 space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className="ri-focus-3-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">面向普通吃瓜网友</span>
                      <span className="mt-1 block text-sm leading-6 text-foreground-600">
                        不追求深度长文，优先保证「一眼看懂今天发生了什么」。
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-secondary-50 text-secondary-700">
                      <i className="ri-layout-masonry-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">结构化而不杂乱</span>
                      <span className="mt-1 block text-sm leading-6 text-foreground-600">
                        用栏目、榜单与标签把信息组织起来，想深入一类话题随时可以点进去。
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-shield-check-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">克制与可信</span>
                      <span className="mt-1 block text-sm leading-6 text-foreground-600">
                        只整理公开信息，对未确认的内容保持明确的时间标注。
                      </span>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100">
          <div className="w-full px-4 md:px-8 py-14 md:py-20">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">发展理念</h2>
            <p className="mt-3 max-w-2xl text-sm md:text-base leading-7 text-foreground-600">
              娱乐资讯的阅读体验，不该被浮夸的标题和没完没了的重复内容消耗掉。
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {values.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-300/60 bg-background-50 p-6">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="w-full px-4 md:px-8 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 lg:gap-12 items-start">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">内容原则</h2>
                <p className="mt-3 text-sm md:text-base leading-7 text-foreground-600">
                  下面几条是我们对所有内容的统一要求，也是你在浏览时可以放心的依据。
                </p>
                <ol className="mt-6 space-y-4">
                  {principles.map((text, index) => (
                    <li key={text} className="flex items-start gap-4">
                      <span className="w-7 h-7 shrink-0 flex items-center justify-center rounded-md bg-accent-100 font-heading text-sm font-bold text-accent-900">
                        {index + 1}
                      </span>
                      <span className="text-sm leading-7 text-foreground-700">{text}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">用户口碑</h2>
                <div className="mt-6 space-y-4">
                  {testimonials.map((item) => (
                    <figure
                      key={item.name}
                      className="rounded-lg border border-background-300/60 bg-background-100 p-5"
                    >
                      <div className="flex items-center gap-1 text-accent-500">
                        {[0, 1, 2, 3, 4].map((star) => (
                          <span key={star} className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-star-fill text-sm" aria-hidden="true"></i>
                          </span>
                        ))}
                      </div>
                      <blockquote className="mt-3 text-sm leading-7 text-foreground-700">{item.text}</blockquote>
                      <figcaption className="mt-4 flex items-center gap-3 border-t border-background-300/60 pt-4">
                        <span className="w-10 h-10 overflow-hidden rounded-full bg-background-200">
                          <img
                            src={item.avatar}
                            alt={`${item.name} 头像`}
                            className="w-full h-full object-cover object-top"
                          />
                        </span>
                        <span>
                          <span className="block text-sm font-medium text-foreground-900">{item.name}</span>
                          <span className="block text-xs text-foreground-500">{item.role}</span>
                        </span>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100">
          <div className="w-full px-4 md:px-8 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12 items-start">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">联系方式</h2>
                <p className="mt-3 text-sm md:text-base leading-7 text-foreground-600">
                  无论是内容反馈还是合作咨询，都可以先通过下面的方式联系我们。我们通常在两个工作日内回复。
                </p>

                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-4 rounded-lg border border-background-300/60 bg-background-50 p-5">
                    <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className="ri-mail-line text-lg" aria-hidden="true"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900">内容反馈与联系</h3>
                      <p className="mt-1 text-sm text-foreground-600">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 rounded-lg border border-background-300/60 bg-background-50 p-5">
                    <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-secondary-50 text-secondary-700">
                      <i className="ri-hand-coin-line text-lg" aria-hidden="true"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900">合作与商务</h3>
                      <p className="mt-1 text-sm text-foreground-600">{siteInfo.cooperation}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 rounded-lg border border-background-300/60 bg-background-50 p-5">
                    <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-time-line text-lg" aria-hidden="true"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900">更新与回复时间</h3>
                      <p className="mt-1 text-sm text-foreground-600">{siteInfo.hours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <ContactForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}